import { Button, ChakraSelect, FormControl, FormLabel, Stack, useDisclosure } from '@elkaso-app/web-design';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  isVerified?: {
    value: string;
    label: string;
  };
};

const verificationOptions = [
  { value: 'true', label: 'Verified' },
  { value: 'false', label: 'Pending Verification' },
];

export const StatusFilterButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setPageParams, getPageParams } = usePageParams();
  const { isVerified } = getPageParams();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<TInputs>();

  const onSubmit = useCallback(
    (data: TInputs) => {
      const currentParams = getPageParams();
      const newParams = { ...currentParams };

      // Handle isVerified parameter properly
      if (data.isVerified?.value !== undefined) {
        const boolValue = data.isVerified.value === 'true';
        newParams.isVerified = boolValue;
      } else {
        // If no option is selected, remove the parameter
        delete newParams.isVerified;
      }

      setPageParams(newParams);
      onClose();
    },
    [setPageParams, getPageParams, onClose]
  );

  const onClear = useCallback(() => {
    const currentParams = getPageParams();
    const newParams = { ...currentParams };
    delete newParams.isVerified;
    setPageParams(newParams);
    reset();
    onClose();
  }, [getPageParams, setPageParams, reset, onClose]);

  useEffect(() => {
    if (isOpen) {
      const selectedVerification =
        isVerified !== undefined && isVerified !== null
          ? verificationOptions.find((option) => option.value === String(isVerified))
          : undefined;

      reset({
        isVerified: selectedVerification,
      });
    }
  }, [isOpen, isVerified, reset]);

  const hasFilters = isVerified !== undefined && isVerified !== null;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Prevent form submission from closing the modal
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={hasFilters}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleFormSubmit} onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <FormLabel>Verification Status</FormLabel>
            <Controller
              name='isVerified'
              control={control}
              render={({ field }) => (
                <ChakraSelect
                  {...field}
                  placeholder='Select verification status...'
                  options={verificationOptions}
                  isClearable
                  onChange={(selectedOption) => {
                    field.onChange(selectedOption);
                  }}
                />
              )}
            />
          </FormControl>

          <Stack direction='row' justify='flex-end' spacing={2}>
            <Button variant='outline' colorScheme='red' onClick={onClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='gray' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='blue' type='submit' isLoading={isSubmitting}>
              Filter
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};
